import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Button, Input, List, Spin, Tooltip, Typography, message } from 'antd'
import { Avatar } from 'antd'
import cls from 'classnames'
import { cloneDeep, isNumber, keyBy, mapValues } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import {
  GetAnniversaryCardsDocument,
  GetAnniversaryCardsQuery,
  UserInfoWithAnniversaryCardFragment,
  useAnniversaryCardCreateMutation,
  useAnniversaryCardUpdateMutation,
} from '@/generated/graphql'
import { routes } from '@/routes'

import s from './anniversary-card.module.less'

type AnniversaryCardType = 'finished' | 'unfinished' | 'blocked'

enum AnniversaryStatus {
  CLOSING = 'CLOSING', // 10 > days
  UPCOMING = 'UPCOMING', // <= 10 days
  OUTDATED = 'OUTDATED',
}

export const AnniversaryCards: React.FC<{
  type: AnniversaryCardType
  anniversary: string
  creatorUserId: number
  loading?: boolean
  data?: GetAnniversaryCardsQuery
}> = ({ type, anniversary, creatorUserId, loading, data }) => {
  const isFinished = type === 'finished'
  const isUnfinished = type === 'unfinished'
  const isBlocked = type === 'blocked'
  const history = useHistory()
  const { t } = useTranslation()
  const [expandedUser, setExpandedUser] = React.useState<number>()
  const [contents, setContents] = React.useState<{ [index: number]: string }>({})

  const [createAnniversaryCard, { loading: createLoading }] = useAnniversaryCardCreateMutation({
    onCompleted: () => {
      message.info(t('anniversaryCard.createCompleted'))
      setExpandedUser(undefined)
    },
    onError: () => {
      message.warn(t('anniversaryCard.createError'))
    },
    refetchQueries: [getOperationName(GetAnniversaryCardsDocument)!],
  })

  const [updateAnniversaryCard, { loading: updateLoading }] = useAnniversaryCardUpdateMutation({
    onCompleted: () => {
      message.info(t('anniversaryCard.updateCompleted'))
      setExpandedUser(undefined)
    },
    refetchQueries: [getOperationName(GetAnniversaryCardsDocument)!],
  })

  const users = (() => {
    switch (type) {
      case 'finished':
        return data?.user?.anniversaryCardSet?.finishedUsers?.edges.map(({ node }) => node) || []
      case 'unfinished':
        return data?.user?.anniversaryCardSet?.unFinishedUsers?.edges.map(({ node }) => node) || []
      case 'blocked':
        return data?.user?.anniversaryCardSet?.blockedUsers?.edges.map(({ node }) => node) || []
    }
  })().sort(
    (x, y) =>
      moment(x.hireDate)
        .year(x.requestedAnniversary ? Number(x.requestedAnniversary) : moment().year())
        .toDate()
        .getTime() -
      moment(y.hireDate)
        .year(y.requestedAnniversary ? Number(y.requestedAnniversary) : moment().year())
        .toDate()
        .getTime()
  )

  React.useEffect(() => {
    if (users.length > 0 && isFinished) {
      const validUsers = users.filter(
        (user) =>
          !!user.anniversaryCards &&
          user.anniversaryCards.some(
            (anniversaryCard) =>
              anniversaryCard.creatorUserId === creatorUserId &&
              anniversaryCard.anniversary === (user.requestedAnniversary || anniversary)
          )
      )
      const contents = mapValues(
        keyBy(validUsers, 'userId'),
        (o) =>
          o.anniversaryCards!.find(
            (anniversaryCard) =>
              anniversaryCard.creatorUserId === creatorUserId &&
              anniversaryCard.anniversary === (o.requestedAnniversary || anniversary)
          )!.content
      )
      setContents(contents)
    }
  }, [data])

  const getAnniversaryStatus = (anniversary: moment.Moment | null, anniversaryYear?: number) => {
    if (!anniversary) {
      return AnniversaryStatus.OUTDATED
    }
    anniversary.year(anniversaryYear || moment().get('year'))
    if (anniversary.isBefore(moment())) {
      return AnniversaryStatus.OUTDATED
    }
    if (anniversary.isBetween(moment().startOf('day'), moment().add(10, 'd').endOf('day'))) {
      return AnniversaryStatus.UPCOMING
    }
    return AnniversaryStatus.CLOSING
  }

  const onExpand = (user: UserInfoWithAnniversaryCardFragment) =>
    setExpandedUser((prev) => {
      if (isNumber(prev) && prev === user.userId) {
        return undefined
      }
      return user.userId
    })

  const onUpsert = async (user: UserInfoWithAnniversaryCardFragment) => {
    if (isFinished) {
      updateAnniversaryCard({
        variables: {
          input: {
            anniversaryCardId: user.anniversaryCards!.find(
              (anniversaryCard) =>
                anniversaryCard.creatorUserId === creatorUserId &&
                anniversaryCard.anniversary === (user.requestedAnniversary || anniversary)
            )!.anniversaryCardId,
            content: contents[user.userId],
          },
        },
      })
    } else {
      createAnniversaryCard({
        variables: {
          input: {
            anniversary: user.requestedAnniversary || anniversary,
            receiverUserId: user.userId,
            content: contents[user.userId],
            creatorUserId,
          },
        },
      })
    }
  }

  const listItem = (user: UserInfoWithAnniversaryCardFragment) => {
    return (
      <div className={s.item}>
        <div className={s.itemHeader}>
          <div className={s.title}>
            <div style={{ display: 'inline-block' }} onClick={() => history.push(routes.users.page(user.email))}>
              <Avatar size={24} src={user.avatarUrl} />
              <a className={cls(s.name)}>{user.name}</a>
            </div>
            {isUnfinished &&
              getAnniversaryStatus(
                user.hireDate ? moment(user.hireDate) : null,
                user.requestedAnniversary ? Number(user.requestedAnniversary) : undefined
              ) === AnniversaryStatus.UPCOMING && (
                <Tooltip
                  title={`距离该成员周年 ${moment(user.hireDate)
                    .year(user.requestedAnniversary ? Number(user.requestedAnniversary) : moment().year())
                    .startOf('day')
                    .diff(moment().startOf('day'), 'days')} 天`}
                >
                  <InfoCircleOutlined className={s.warningIcon} />
                </Tooltip>
              )}
          </div>
          {!isBlocked && (
            <div className={s.itemAction}>
              {user.hireDate
                ? `${
                    (user.requestedAnniversary ? Number(user.requestedAnniversary) : moment().year()) -
                    moment(user.hireDate).year()
                  } ${t('anniversaryCard.year')}`
                : t('project.na')}
              {'，'}
              {moment(user.hireDate)
                .year(user.requestedAnniversary ? Number(user.requestedAnniversary) : moment().year())
                .format('MM-DD dddd')}
              <RightOutlined
                onClick={() => onExpand(user)}
                className={cls(s.expandIcon, { [s.expand]: expandedUser === user.userId })}
              />
            </div>
          )}
        </div>
        {expandedUser === user.userId && (
          <Input.TextArea
            value={contents[user.userId] || ''}
            onChange={(e) => {
              const newContents = cloneDeep(contents)
              newContents[user.userId] = e.target.value
              setContents(newContents)
            }}
            className={s.input}
            autoSize={false}
            rows={6}
            maxLength={160}
            showCount
          />
        )}
        {expandedUser === user.userId && (
          <Button
            className={s.actionButton}
            loading={createLoading || updateLoading}
            disabled={!contents[user.userId]}
            onClick={() => onUpsert(user)}
            type='primary'
          >
            {t(`anniversaryCard.${isFinished ? 'update' : 'create'}`)}
          </Button>
        )}
      </div>
    )
  }

  const header = (
    <div>
      <Typography.Title level={5}>
        {t(`anniversaryCard.system.${isFinished ? 'finished' : isUnfinished ? 'unfinished' : 'blocked'}`)}
      </Typography.Title>
      {isBlocked && <Typography.Text type='secondary'>可以进入TA的个人主页发送感谢帖子～</Typography.Text>}
    </div>
  )

  const list = (
    <List>
      {users.map((user) => (
        <List.Item key={user.userId}>{listItem(user)}</List.Item>
      ))}
    </List>
  )

  return (
    <div className={s.anniversaryUserContainer}>
      {header}
      {loading ? (
        <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin delay={300} size='large' />
        </div>
      ) : (
        list
      )}
    </div>
  )
}
